// Saved themes. Kept on the organisation's branding row on the server (see
// BrandingContext's themeLibrary/saveThemeLibrary), so every admin sees and
// edits the same list - not just the browser that saved it. Export and
// import still move themes between organisations, or into a file to keep.
import { useCallback } from 'react'
import { TOKEN_KEYS, isHex, sameColor } from './settingsModel'

export const THEME_FILE_FORMAT = 'xtract-theme'
export const MAX_THEME_NAME = 40

export const swatchesFor = theme => theme.swatches || [
  theme.primary_color, theme.tokens.primary, theme.tokens.success, theme.tokens.warning, theme.tokens.error, theme.tokens.info,
]

export const themeMatches = (theme, primaryColor, tokens) =>
  sameColor(theme.primary_color, primaryColor) && TOKEN_KEYS.every(key => sameColor(theme.tokens[key], tokens[key]))

const newId = () => `theme_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`

// A theme with every token present and valid, or null. Missing or bad tokens fall back to defaults.
export function normalizeTheme(raw, tokenDefaults) {
  if (!raw || typeof raw !== 'object') return null
  const name = typeof raw.name === 'string' ? raw.name.trim().slice(0, MAX_THEME_NAME) : ''
  if (!name || !isHex(raw.primary_color)) return null
  const source = raw.tokens && typeof raw.tokens === 'object' ? raw.tokens : {}
  const tokens = Object.fromEntries(TOKEN_KEYS.map(key => [key, isHex(source[key]) ? source[key] : tokenDefaults[key]]))
  const now = new Date().toISOString()
  return {
    id: typeof raw.id === 'string' && raw.id ? raw.id : newId(),
    name,
    primary_color: raw.primary_color,
    tokens,
    created_at: raw.created_at || now,
    updated_at: raw.updated_at || now,
  }
}

export function createTheme({ name, primary_color, tokens }, tokenDefaults) {
  return normalizeTheme({ name, primary_color, tokens, id: newId() }, tokenDefaults)
}

// "Ocean" -> "Ocean 2" when "Ocean" is taken. Case-insensitive.
export function uniqueName(name, taken, ignoreId) {
  const base = (name || '').trim().slice(0, MAX_THEME_NAME) || 'Custom theme'
  const used = new Set(taken.filter(t => t.id !== ignoreId).map(t => t.name.toLowerCase()))
  if (!used.has(base.toLowerCase())) return base
  for (let n = 2; ; n += 1) {
    const candidate = `${base.slice(0, MAX_THEME_NAME - String(n).length - 1)} ${n}`
    if (!used.has(candidate.toLowerCase())) return candidate
  }
}

export function nameError(name, taken, ignoreId) {
  const trimmed = (name || '').trim()
  if (!trimmed) return 'Enter a name.'
  if (trimmed.length > MAX_THEME_NAME) return `Keep it under ${MAX_THEME_NAME} characters.`
  if (taken.some(t => t.id !== ignoreId && t.name.toLowerCase() === trimmed.toLowerCase())) return 'A theme with this name already exists.'
  return null
}

// File contents for one theme or several.
export function exportThemes(themes) {
  const strip = ({ name, primary_color, tokens }) => ({ name, primary_color, tokens })
  return JSON.stringify({ format: THEME_FILE_FORMAT, version: 1, themes: themes.map(strip) }, null, 2)
}

// Themes from an exported file. Accepts { themes: [...] }, a single theme, or a
// bare array. Throws with a readable message when nothing usable is inside.
export function parseThemeFile(text, tokenDefaults) {
  let data
  try {
    data = JSON.parse(text)
  } catch {
    throw new Error("This file isn't valid JSON.")
  }
  const list = Array.isArray(data) ? data : Array.isArray(data?.themes) ? data.themes : [data]
  const themes = list.map(raw => normalizeTheme({ ...raw, id: undefined }, tokenDefaults)).filter(Boolean)
  if (!themes.length) throw new Error('No themes found. Each theme needs a name and a 6-digit hex primary colour.')
  return themes
}

// Wraps the org's shared theme list (from useBranding()) in the same
// {themes, add, update, remove} shape the section used with its old,
// browser-only storage - but backed by the server, so add/update/remove are
// async and resolve to false (never throw) when the save fails.
export function useSharedThemeLibrary(themes, saveThemeLibrary) {
  const commit = useCallback(async next => {
    try {
      await saveThemeLibrary(next)
      return true
    } catch {
      return false
    }
  }, [saveThemeLibrary])

  const add = useCallback(items => commit([...themes, ...items]), [commit, themes])
  const update = useCallback((id, changes) => commit(themes.map(t => (
    t.id === id ? { ...t, ...changes, updated_at: new Date().toISOString() } : t
  ))), [commit, themes])
  const remove = useCallback(id => commit(themes.filter(t => t.id !== id)), [commit, themes])

  return { themes, add, update, remove }
}
